/**
 * @fileoverview Componente responsável por renderizar a identidade visual dinâmica da empresa.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';

// Importa os vídeos animados baseados no tema
import lightVideo from '../../assets/videos/Logo_Animado_Sttelar_Atomo_Fundo_Branco.mp4';
import darkVideo from '../../assets/videos/Logo_Animado_Sttelar_Atomo_Fundo_Preto.mp4';

const DynamicLogo = ({ className, style, alt = "logo", ...props }) => {
  const theme = useTheme();
  const videoRef = useRef(null);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(theme.mode);
  
  // Seleciona o vídeo baseado no tema atual
  const videoSrc = theme.mode === 'light' ? lightVideo : darkVideo;
  
  // Reset do estado quando o tema muda
  useEffect(() => {
    if (currentTheme !== theme.mode) {
      setCurrentTheme(theme.mode);
      setHasPlayed(false);
      setIsLoaded(false);
    }
  }, [theme.mode, currentTheme]);
  
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsLoaded(true);
    };

    const handleEnded = () => {
      setHasPlayed(true);
      // Pausa o vídeo no último frame
      video.pause();
    };

    const handleCanPlay = () => {
      // Reproduz o vídeo automaticamente quando carregado
      if (!hasPlayed) {
        video.play().catch(console.error);
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [hasPlayed, videoSrc]);

  return (
    <video
      key={`logo-${theme.mode}`} // Força recriação quando tema muda
      ref={videoRef}
      className={className}
      style={{
        ...style,
        objectFit: 'contain',
        pointerEvents: 'none', // Evita interações com o vídeo
        // Correção para eliminar bordas fantasma - dimensões múltiplas de 16
        width: '480px', // 480÷16=30 ✅
        height: '272px', // 272÷16=17 ✅ (mais próximo de 16:9)
        maxWidth: '480px',
        maxHeight: '272px',
        borderRadius: '999px', // Bordas arredondadas ao MÁXIMO
        overflow: 'hidden', // Garante que o conteúdo respeite o borderRadius
      }}
      muted
      playsInline
      preload="metadata"
      {...props}
    >
      <source src={videoSrc} type="video/mp4" />
    </video>
  );
};

export default DynamicLogo;
